import React, { PropsWithChildren, use, useEffect, useMemo, useRef } from "react";
import NetInfo, { useNetInfo } from "@react-native-community/netinfo";
import { AppState, Platform, StyleSheet } from "react-native";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import ScreenTitle from "../components/ScreenTitle";
import Modal from "../components/Modal";
import ScreenCard from "../screens/ScreenCard";
import MarkdownView from "../components/MarkdownView";
import apolloClient from "../configs/graphql.config";
import Icon from "../components/Icon";
import Gap from "../ui_atoms/Gap";
import { AuthManagerContext } from "./AuthManager";

const NetworkManager: React.FC<PropsWithChildren> = ({ children }) => {
    // Track previous connectivity in a ref — we only need it to detect the
    // false→true transition (reconnection), not to influence rendering.
    const wasConnectedRef = useRef<boolean>(true);
    const i18n = useI18n();
    const styles = useStyles();
    const { user, refreshUser } = use(AuthManagerContext);
    const netInfoState = useNetInfo();

    useEffect(() => {
        if (netInfoState.isInternetReachable === null) {
            return;
        }
        const wasConnected = wasConnectedRef.current;
        wasConnectedRef.current = netInfoState.isInternetReachable;
        if (!wasConnected && netInfoState.isInternetReachable) {
            // eslint-disable-next-line no-console
            console.log("Internet connection restored. Re-run apollo queries");
            void apolloClient.refetchQueries({ include: "active" });
            // in case connection was lost during fetching user data we need to refetch the user
            if (!user) {
                void refreshUser?.();
            }
        }
    }, [netInfoState, refreshUser, user]);

    useEffect(() => {
        const subAppState = AppState.addEventListener("change", (nextAppState) => {
            if (Platform.OS === "ios" && nextAppState == "active") {
                void NetInfo.refresh();
            }
        });
        return () => {
            if (subAppState) {
                subAppState.remove();
            }
        };
    }, []);
    return (
        <>
            {/* `Modal` is hidden unless `visible` is passed. Kept mounted and driven by `visible`
                (rather than conditionally rendered) so it closes with its exit animation once the
                connection is back. No `onClose` on purpose: without it there is no close button,
                the iOS sheet cannot be swiped away, and Android back / the web backdrop do nothing,
                so the screen stays up for as long as the device is offline. `null` (reachability
                not determined yet, e.g. at startup) is not treated as offline. */}
            <Modal visible={netInfoState.isInternetReachable === false}>
                <ScreenTitle style={styles.title}>{i18n.t("no_network")}</ScreenTitle>
                <ScreenCard style={styles.screenCard}>
                    <Gap gap={2} />
                    <Icon style={{ alignSelf: "center" }} name="wifi-slash" size={165} />
                    <Gap gap={2} />
                    <MarkdownView markdown={i18n.t("network_will_reconnect")} />
                </ScreenCard>
            </Modal>
            {children}
        </>
    );
};

const useStyles = () => {
    const { gridSize, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                title: {
                    ...fonts.titleSmall,
                    alignSelf: "center",
                    marginTop: gridSize / 1.5,
                    marginBottom: gridSize,
                },
                screenCard: {
                    paddingHorizontal: gridSize / 1.5,
                },
            }),
        [fonts.titleSmall, gridSize],
    );
};

export default NetworkManager;
